import { Button } from "@/components/ui/button"
import { useGetIntegrations, useGetAuthData } from "@/lib/hooks"
import { cn } from "@/lib/utils"
import { ArrowRightIcon } from "@heroicons/react/16/solid"
import { CheckBadgeIcon } from "@heroicons/react/24/outline"
import { Link, useNavigate } from "@tanstack/react-router"
import type { IntegrationStatus } from "../../../api/src/lib/integrations"
import { client } from "@/lib/ts-rest"
import { Timetable } from "./integration-sections/timetable"
import { ExclamationTriangleIcon, PauseCircleIcon } from "@heroicons/react/24/outline"
import { useEffect, useRef } from "react"
import { trackEvent } from "@/lib/log"

export function Integrations() {
  const { data, error, refetch, user } = useGetAuthData()
  const {
    data: integrationsResponse,
    refetch: refetchIntegrations,
    isLoading: isLoadingIntegrations,
  } = useGetIntegrations()
  const navigate = useNavigate()
  const { mutate: updateIntegration, isPending: isPendingUpdateIntegration } =
    client.updateIntegration.useMutation({
      onSuccess: () => {
        refetchIntegrations()
      },
    })
  // eslint-disable-next-line
  let connectionCards = [
    {
      logoUrl: "/amazon-alexa.svg",
      description:
        "Connect your Alexa devices with the Pillars platform to hear the athan throughout your home!",
      button: true,
      pictureUrl: "/amazon-alexa.png",
      pictureWidth: 230,
    },
    {
      logoUrl: "/google-home.svg",
      description:
        "Connect your Google Home devices with the Pillars platform to hear the athan throughout your home!",
      pictureUrl: "/google-home.png",
      pictureWidth: 160,
    },
    {
      logoUrl: "/apple-homepod.svg",
      description:
        "Connect your Homepod devices with the Pillars platform to hear the athan throughout your home!",
      pictureUrl: "/apple-homepod.png",
      pictureWidth: 160,
    },
  ]

  const integrations = integrationsResponse?.body.integrations

  if (integrations?.length) {
    connectionCards = []
  }

  const errorIdToMessage: Record<
    NonNullable<IntegrationStatus["errorId"]>,
    string
  > = {
    noLocation: "Location not set up.",
    setupIncomplete: "Alexa setup instructions incomplete.",
    notPaying: "Your subscription is not active.",
    noAlexaAccount: "Alexa setup instructions incomplete.",
  }

  const autoNavigated = useRef(false)
  useEffect(() => {
    if (autoNavigated.current || !integrations?.length) return
    const erroredIntegration = integrations.find(
      (i) => i.status.type === "errored"
    )
    if (erroredIntegration) {
      autoNavigated.current = true
      navigate({
        to: "/integrations/$integrationId",
        params: { integrationId: String(erroredIntegration.integration.id) },
      })
    }
  }, [integrations, navigate])

  if (isLoadingIntegrations) return

  return (
    <>
      <div className="flex md:items-center flex-col md:flex-row gap-3 mb-5">
        <div className="text-primary font-medium md:text-3xl text-2xl">
          Connections
        </div>
        {user?.hasSpeakerSubscription && (
          <div className="bg-green-50 border border-green-200 p-2 rounded-lg w-fit flex items-center gap-1">
            <CheckBadgeIcon className="h-4 w-4 text-green-700 shrink-0" />
            <div className="text-green-700 text-xs font-medium">
              Subscription active
            </div>
          </div>
        )}
      </div>
      <div className="mb-10">
        Manage the connections between Pillars and your smart speakers.
      </div>
      <div className="flex flex-col gap-5">
        {integrations?.map(
          ({
            status: { type, errorId },
            locationAddress,
            locationName,
            integration: { id },
          }) => (
            <div
              key={id}
              className="rounded-3xl bg-white flex flex-col md:flex-row p-6 gap-6 justify-between"
            >
              <div className="flex flex-col justify-between flex-grow gap-4 md:gap-0">
                <div>
                  <img src="/amazon-alexa.svg" className="h-8" />
                </div>
                <div className="flex flex-col gap-3 md:mt-auto">
                  {type === "active" ? (
                    <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-xl p-4 w-fit">
                      <CheckBadgeIcon className="h-6 w-6 shrink-0" />
                      <div>
                        <span className="font-medium">Connection active</span>
                        {locationName && (
                          <div className="text-sm text-green-600">{locationName}</div>
                        )}
                      </div>
                    </div>
                  ) : type === "paused" ? (
                    <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-4 w-fit">
                      <PauseCircleIcon className="h-6 w-6 shrink-0" />
                      <div>
                        <span className="font-medium">Connection paused</span>
                        {locationName && (
                          <div className="text-sm text-amber-600">{locationName}</div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-xl p-4 w-fit">
                      <ExclamationTriangleIcon className="h-6 w-6 shrink-0" />
                      <div>
                        <span className="font-medium">Connection inactive</span>
                        <div className="text-sm text-red-600">
                          {errorId
                            ? errorIdToMessage[errorId]
                            : "Unknown error"}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      variant={type === "paused" ? "default" : "destructive"}
                      onClick={() => {
                        const action = type === "paused" ? "resume" : "pause"
                        trackEvent(action === "pause" ? "paused an integration" : "resumed an integration", { integrationId: id })
                        updateIntegration({
                          body: {
                            integration: {
                              paused: type === "paused" ? false : true,
                            },
                          },
                          params: { integrationId: String(id) },
                        })
                      }}
                      disabled={isPendingUpdateIntegration}
                      className={cn(
                        "w-fit",
                        type === "paused" && "bg-green-600 hover:bg-green-700"
                      )}
                    >
                      {type === "paused" ? "Resume" : "Pause"}
                    </Button>
                    <Link
                      key={id}
                      to={"/integrations/$integrationId"}
                      params={{ integrationId: String(id) }}
                    >
                      <Button variant="secondary">Configure</Button>
                    </Link>
                  </div>
                </div>
              </div>
              {locationName && (
                <div>
                  <Timetable integrationId={id} />
                </div>
              )}
            </div>
          )
        )}
        {connectionCards.map(
          ({ button, description, logoUrl, pictureUrl, pictureWidth }) => (
            <div
              key={logoUrl}
              className={cn(
                "rounded-3xl bg-white flex gap-5",
                !button && "opacity-70"
              )}
            >
              <div className="p-6 flex flex-col gap-5 flex-grow">
                <div>
                  <img src={logoUrl} className="h-8" />
                </div>
                <div className="max-w-[300px]">{description}</div>
                <div className="w-fit">
                  {button ? (
                    <SetUpButton />
                  ) : (
                    <Button variant="outline">Coming Soon</Button>
                  )}
                </div>
              </div>
              <div className="relative h-auto w-72 overflow-hidden">
                <div className="w-72 flex justify-center absolute bottom-0 right-0 -mb-7 pr-6">
                  <img
                    src={pictureUrl}
                    style={{
                      width: pictureWidth + "px",
                    }}
                  />
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </>
  )
}

function SetUpButton() {
  const navigate = useNavigate()
  const { mutate, isPending } = client.createIntegration.useMutation({
    onSuccess: (response) => {
      trackEvent("started setting up an integration", { integrationId: response.body.integrationId })
      navigate({
        to: "/integrations/$integrationId",
        params: { integrationId: String(response.body.integrationId) },
      })
    },
  })

  return (
    <Button onClick={() => mutate({})} disabled={isPending}>
      {isPending ? "Setting up..." : "Set up"}
      {!isPending && <ArrowRightIcon className="w-4 h-4 ml-2" />}
    </Button>
  )
}
